/**
 * Qué se consulta: de qué choferes y de qué fechas.
 *
 * El **chofer propio** pertenece a una de las empresas del grupo y se liquida
 * por mes y año. El **eventual** trabaja para cualquier otra empresa y se
 * consulta por un intervalo de fechas, que se liquida en el mes en que
 * **cierra** (RRHH, 13-10-2026).
 *
 * Pueden ir las dos juntas: cada una trae su ventana y las novedades se
 * revisan y se cargan en la misma pantalla. Lo que no hay es un valor por
 * omisión — mientras no se elija al menos una, no se muestra período ni se
 * deja consultar, porque de qué choferes se habla cambia a quién se le paga.
 */

import type { RespuestaConsulta, TipoChofer } from '../../api';
import type { SeleccionPeriodo } from './api';
import { problemaDelRango, textoDeRango } from './rango';

/** El orden en que se muestran y se mandan, para que no dependa del clic. */
const ORDEN: readonly TipoChofer[] = ['PROPIO', 'EVENTUAL'];

const ordenados = (tipos: ReadonlySet<TipoChofer>): readonly TipoChofer[] =>
  ORDEN.filter((t) => tipos.has(t));

/**
 * La selección lista para mandar, o `null` si todavía está incompleta.
 *
 * Devolver `null` es lo que apaga el botón: una consulta a medio armar
 * traería los tramos de otras fechas sin que nadie lo note.
 */
export function seleccionDe(
  tipos: ReadonlySet<TipoChofer>,
  periodo: { readonly anio: number; readonly mes: number },
  intervalo: { readonly desde: string; readonly hasta: string },
  manual: { readonly desde: string; readonly hasta: string } = { desde: '', hasta: '' },
): SeleccionPeriodo | null {
  const tiposChofer = ordenados(tipos);
  if (tiposChofer.length === 0) return null;

  const conPropios = tiposChofer.includes('PROPIO');
  const conEventuales = tiposChofer.includes('EVENTUAL');

  if (conEventuales && problemaDelRango(intervalo.desde, intervalo.hasta) !== null) return null;

  /*
   * El mes solo se pide cuando hay propios. Con eventuales solos el período
   * de liquidación lo deduce el servidor del día de cierre, así que mandar un
   * mes acá sería mandar un dato que nadie eligió.
   */
  if (conPropios) {
    const { anio, mes } = periodo;
    if (!Number.isInteger(anio) || anio < 2000 || anio > 2100) return null;
    if (!Number.isInteger(mes) || mes < 1 || mes > 12) return null;
  }

  /*
   * La fecha de los conceptos manuales es opcional: sin ella la consulta corre
   * igual y esos conceptos quedan sin datos y sin poder tildarse. A medio
   * escribir no viaja, porque traería otro período sin que nadie lo note.
   */
  const conManual =
    manual.desde !== '' &&
    manual.hasta !== '' &&
    problemaDelRango(manual.desde, manual.hasta) === null;

  return {
    tiposChofer,
    ...(conPropios ? { anio: periodo.anio, mes: periodo.mes } : {}),
    ...(conEventuales ? { desde: intervalo.desde, hasta: intervalo.hasta } : {}),
    ...(conManual ? { rangoDesde: manual.desde, rangoHasta: manual.hasta } : {}),
  };
}

/**
 * La selección con la que se hizo la consulta que está en pantalla.
 *
 * El Excel y el impacto salen de acá y no de los controles: los controles
 * pueden haberse movido sin volver a consultar, y la planilla tiene que traer
 * los números que se están mirando.
 */
export function seleccionConsultada(consulta: RespuestaConsulta): SeleccionPeriodo {
  const tiposChofer = consulta.tiposChofer;
  const conPropios = tiposChofer.includes('PROPIO');

  return {
    tiposChofer,
    ...(conPropios
      ? { anio: Number(consulta.periodo.slice(0, 4)), mes: Number(consulta.periodo.slice(5, 7)) }
      : {}),
    ...(consulta.intervalo === null
      ? {}
      : { desde: consulta.intervalo.desde, hasta: consulta.intervalo.hasta }),
    ...(consulta.rangoManual === null
      ? {}
      : { rangoDesde: consulta.rangoManual.desde, rangoHasta: consulta.rangoManual.hasta }),
  };
}

/** Cómo se nombran las poblaciones consultadas, en una línea. */
export function nombreDeLosTipos(tipos: readonly TipoChofer[]): string {
  const nombres = ORDEN.filter((t) => tipos.includes(t)).map((t) =>
    t === 'PROPIO' ? 'propios' : 'eventuales',
  );
  if (nombres.length === 0) return '';
  return `choferes ${nombres.join(' y ')}`;
}

/**
 * De qué fechas son los números que están en pantalla, para el sello de la
 * banda. Con eventuales no alcanza con el mes: las cantidades salen del
 * intervalo, que puede empezar en otro mes.
 */
export function textoDeLoConsultado(consulta: RespuestaConsulta): string {
  if (consulta.intervalo === null) return '';
  return textoDeRango(consulta.intervalo);
}
